export interface Time {
  hours: number;
  minutes: number;
  seconds: number;
}

export class DateUtils {
  // Bounds for timestamps this app will trust as message cursors.
  // Max mirrors .NET DateTime.MaxValue's year (9999); min is the Unix epoch.
  public static readonly MinPlausibleTimestampMs = 0;
  public static readonly MaxPlausibleTimestampMs = Date.UTC(9999, 11, 31, 23, 59, 59, 999);

  public static isoToMs(isoDate: string) {
    return new Date(isoDate).valueOf();
  }

  // Parses an ISO timestamp to epoch ms; returns undefined when unparseable or outside
  // the plausible range (guards against out-of-range values like expanded-year ISO dates,
  // which parse fine in JS but are unrepresentable server-side).
  public static toPlausibleMs(isoDate: string): number | undefined {
    const ms = new Date(isoDate).valueOf();

    if (
      Number.isNaN(ms) ||
      ms < DateUtils.MinPlausibleTimestampMs ||
      ms > DateUtils.MaxPlausibleTimestampMs
    ) {
      return undefined;
    }

    return ms;
  }

  public static getDateXDaysAgo(numDays: number): Date {
    const today = new Date();
    const fiveDaysAgo = new Date();
    fiveDaysAgo.setDate(today.getDate() - numDays);
    return fiveDaysAgo;
  }

  public static isAfterDate(date: Date, compareDate: Date): boolean {
    return date > compareDate;
  }

  public static getRelativeFutureDate(dateString: string) {
    const clearAfterDate = new Date(dateString);
    const now = new Date();
    const isToday = clearAfterDate.getDate() === now.getDate();

    // Format the time as "hh:mm AM/PM"
    const formatTime = (d: Date): string =>
      d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

    // Format the date as "MM/DD/YYYY hh:mm AM/PM"
    const formatDate = (d: Date): string => d.toLocaleDateString('en-US') + ' ' + formatTime(d);

    const dateTimeString = isToday ? formatTime(clearAfterDate) : formatDate(clearAfterDate);

    return dateTimeString;
  }

  public static getTime(date: Date): Time {
    return {
      hours: date.getHours(),
      minutes: date.getMinutes(),
      seconds: date.getSeconds(),
    };
  }

  public static formatTime(time: Time): string {
    const isAm = time.hours < 12;
    const hours = time.hours % 12 || 12;
    const minutes = time.minutes.toString().padStart(2, '0');

    return `${hours}:${minutes} ${isAm ? 'AM' : 'PM'}`;
  }

  public static combineDateAndTime(date: Date, time: Time): Date {
    const newDate = new Date(date);
    newDate.setHours(time.hours);
    newDate.setMinutes(time.minutes);
    newDate.setSeconds(time.seconds);
    return newDate;
  }

  // Formats a due date relative to `now`. Pure — pass `now` from a midnight-ticking signal so
  // the rendered string refreshes across day boundaries.
  //   includesTime true:  "today 9:30AM" | "yesterday 9:30AM" | "tomorrow 9:30AM" | "Mon 9:30AM" | "5/22/2026 9:30AM"
  //   includesTime false: "today" | "yesterday" | "tomorrow" | "Mon" | "5/22/2026"
  public static formatRelativeDueDate(due: Date, now: Date, includesTime = true): string {
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const startOfDue = new Date(due);
    startOfDue.setHours(0, 0, 0, 0);

    const msPerDay = 24 * 60 * 60 * 1000;
    const dayDiff = Math.round((startOfDue.getTime() - startOfToday.getTime()) / msPerDay);

    const hours12 = due.getHours() % 12 || 12;
    const minutes = due.getMinutes().toString().padStart(2, '0');
    const meridiem = due.getHours() < 12 ? 'AM' : 'PM';
    const timeSuffix = includesTime ? ` ${hours12}:${minutes}${meridiem}` : '';

    if (dayDiff === 0) {
      return `Today${timeSuffix}`;
    }
    if (due < now) {
      if (dayDiff === -1) {
        return `Yesterday${timeSuffix}`;
      }
      return `${due.toLocaleDateString('en-US')}${timeSuffix}`;
    }
    if (dayDiff === 1) {
      return `Tomorrow${timeSuffix}`;
    }
    if (dayDiff <= 6) {
      const weekday = due.toLocaleDateString('en-US', { weekday: 'short' });
      return `${weekday}${timeSuffix}`;
    }
    return `${due.toLocaleDateString('en-US')}${timeSuffix}`;
  }

  public static getOrdinalSuffix(day: number): string {
    const lastTwo = day % 100;
    if (lastTwo >= 11 && lastTwo <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  // Formats a message-divider date relative to `today`. Pure — pass `today` from a
  // midnight-ticking signal so the rendered string refreshes across day boundaries.
  //   "Today" | "Yesterday" | "Saturday, August 15th" (current year) | "August 15, 2025"
  public static formatMessageDividerDate(date: Date, today: Date): string {
    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);
    const startOfDate = new Date(date);
    startOfDate.setHours(0, 0, 0, 0);

    const msPerDay = 24 * 60 * 60 * 1000;
    const dayDiff = Math.round((startOfDate.getTime() - startOfToday.getTime()) / msPerDay);

    if (dayDiff === 0) {
      return 'Today';
    }
    if (dayDiff === -1) {
      return 'Yesterday';
    }
    if (date.getFullYear() === today.getFullYear()) {
      const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
      const month = date.toLocaleDateString('en-US', { month: 'long' });
      return `${weekday}, ${month} ${date.getDate()}${DateUtils.getOrdinalSuffix(date.getDate())}`;
    }
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
}
